import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import {
  CRUDLayout,
  Alert,
  DataStatus,
  ActionButton,
  InputSearch,
  Pagination,
  Table,
  THead,
  TBody,
  Tr,
  ThFixed,
  TdFixed,
  Th,
  Td,
  ApprovalStatusButton,
} from "components";
import {
  RupiahConvert,
  DateConvert,
  PageNumber as TableNumber,
} from "utilities";
import { PenerimaanPettyCashApi } from "api";

const HistoryApprovalPenerimaanPettyCash = () => {
  const history = useHistory();
  const location = useLocation();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [data, setData] = useState([]);
  const [paginationConfig, setPaginationConfig] = useState({
    page: "1",
    dataLength: "10",
    totalPage: "1",
    dataCount: "0",
  });
  const [searchConfig, setSearchConfig] = useState({
    status: false,
    key: "",
  });
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    variant: "primary",
    text: "",
  });
  const STATUS_APPROVAL = {
    APP: ["outline-success", "APPROVED"],
    VER: ["outline-success", "VERIFIED"],
    REV: ["outline-warning", "REVISE"],
    REJ: ["outline-danger", "REJECTED"],
    PEN: ["outline-secondary", "PENDING"],
  };

  const getInitialData = () => {
    setIsPageLoading(true);

    PenerimaanPettyCashApi.getHistory({
      q: searchConfig.key,
      page: paginationConfig.page,
      per_page: paginationConfig.dataLength,
    })
      .then(({ data }) => {
        setData(data?.data ?? []);
        setPaginationConfig({
          ...paginationConfig,
          dataCount: data.data_count,
          totalPage: data.total_page,
        });
        searchConfig.status &&
          setAlertConfig({
            show: true,
            variant: "primary",
            text: `Hasil dari pencarian: ${searchConfig.key}`,
          });
      })
      .catch((err) => {
        setAlertConfig({
          show: true,
          variant: "danger",
          text: "Data gagal dimuat!",
        });
      })
      .finally(() => setIsPageLoading(false));
  };

  const checkAlert = () => {
    const locationState = location.state;

    if (locationState) {
      if (locationState.alert) {
        setAlertConfig({
          show: locationState.alert.show,
          text: locationState.alert.text,
          variant: locationState.alert.variant,
        });
      }
    }
  };

  useEffect(() => {
    getInitialData();
    checkAlert();

    return () => {
      setIsPageLoading(false);
    };
  }, [searchConfig.key, paginationConfig.page, paginationConfig.dataLength]);

  const PageContent = () => {
    const DataTable = () => (
      <Table>
        <THead>
          <Tr>
            <ThFixed>No</ThFixed>
            <ThFixed>Informasi Pembuat Pengajuan</ThFixed>
            <ThFixed>Informasi Penerimaan Petty Cash</ThFixed>
            <ThFixed>Informasi Bukti Mutasi</ThFixed>
            <Th width={150}>Unit Produksi</Th>
            <Th width={150}>Nominal Penerimaan Petty Cash</Th>
            <Th width={180}>Keterangan</Th>
            <ThFixed>Status Approval</ThFixed>
            <Th style={{ width: "8.5rem" }}>Aksi</Th>
          </Tr>
        </THead>
        <TBody>
          {data?.map((val, index) => (
            <Tr key={index}>
              <TdFixed>
                {TableNumber(
                  paginationConfig.page,
                  paginationConfig.dataLength,
                  index
                )}
              </TdFixed>
              <TdFixed>
                <div>{val.nama_karyawan_pengaju ?? "-"}</div>
                <div>{val.nama_jabatan_pengaju ?? "-"}</div>
              </TdFixed>
              <TdFixed>
                <div>
                  {val.tgl_penerimaan_petty_cash
                    ? DateConvert(new Date(val.tgl_penerimaan_petty_cash))
                        .defaultDMY
                    : "-"}
                </div>
                <div>{val.no_penerimaan_petty_cash ?? "-"}</div>
              </TdFixed>
              <TdFixed>
                <div>
                  {val.tgl_mutasi_petty_cash
                    ? DateConvert(new Date(val.tgl_mutasi_petty_cash))
                        .defaultDMY
                    : "-"}
                </div>
                <div>{val.no_bukti_mutasi ?? "-"}</div>
              </TdFixed>
              <Td>{val.nama_unit_produksi ?? "-"}</Td>
              <Td textRight>
                {
                  RupiahConvert(
                    String(parseInt(val.nominal_penerimaan_petty_cash ?? 0))
                  ).detail
                }
              </Td>
              <Td>{val.keterangan ?? "-"}</Td>
              <Td>
                <ApprovalStatusButton
                  variant={STATUS_APPROVAL[val.status_approval ?? "PEN"][0]}
                >
                  {STATUS_APPROVAL[val.status_approval ?? "PEN"][1]}
                </ApprovalStatusButton>
              </Td>
              <Td>
                <div className="d-flex justify-content-center">
                  <ActionButton
                    size="sm"
                    text="Lihat Detail"
                    className="col"
                    onClick={() =>
                      history.push(
                        `/human-resource/approval/penerimaan-petty-cash/detail/${val.id_penerimaan_petty_cash}`,
                        {
                          tab: "history",
                          no_penerimaan_petty_cash:
                            val.no_penerimaan_petty_cash,
                        }
                      )
                    }
                  />
                </div>
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    );

    return (
      <>
        <DataTable />
        {!searchConfig.status && (
          <Pagination
            dataLength={paginationConfig.dataLength}
            dataNumber={
              paginationConfig.page * paginationConfig.dataLength -
              paginationConfig.dataLength +
              1
            }
            dataPage={
              paginationConfig.dataCount < paginationConfig.dataLength
                ? paginationConfig.dataCount
                : paginationConfig.page * paginationConfig.dataLength
            }
            dataCount={paginationConfig.dataCount}
            currentPage={paginationConfig.page}
            totalPage={paginationConfig.totalPage}
            onPaginationChange={({ selected }) =>
              setPaginationConfig({
                ...paginationConfig,
                page: selected + 1,
              })
            }
            onDataLengthChange={(e) =>
              setPaginationConfig({
                ...paginationConfig,
                page: 1,
                dataLength: e.target.value,
              })
            }
          />
        )}
      </>
    );
  };

  return (
    <CRUDLayout>
      {/* head section */}
      <CRUDLayout.Head>
        <CRUDLayout.HeadSearchSection>
          <Row className="mb-2">
            <Col md={8}>
              <InputSearch
                placeholder="Cari RAE"
                value={searchConfig.key}
                onChange={(e) => {
                  const key = e.target.value;
                  setSearchConfig({
                    ...searchConfig,
                    key: e.target.value,
                  });
                  setAlertConfig({
                    show: key ? true : false,
                    variant: "primary",
                    text: "Hasil dari pencarian: " + key,
                  });
                }}
              />
            </Col>
          </Row>
        </CRUDLayout.HeadSearchSection>
        <CRUDLayout.HeadButtonSection></CRUDLayout.HeadButtonSection>
      </CRUDLayout.Head>

      {/* alert */}
      <Alert
        show={alertConfig.show}
        variant={alertConfig.variant}
        text={alertConfig.text}
        showCloseButton={true}
        onClose={() =>
          setAlertConfig({
            ...alertConfig,
            show: false,
          })
        }
      />

      {/* content */}
      {isPageLoading ? (
        <DataStatus loading={true} text="Memuat data . . ." />
      ) : data ? (
        data.length > 0 ? (
          <PageContent />
        ) : (
          <DataStatus text="Tidak ada data" />
        )
      ) : (
        <DataStatus text="Data gagal dimuat" />
      )}
    </CRUDLayout>
  );
};

export default HistoryApprovalPenerimaanPettyCash;
